/**
 * Data Share — the quick first check every donated conversation goes through.
 *
 * Runs in the browser for export files (nothing leaves the device) and on the server for share
 * links, so both paths judge the same way. It is a FIRST PASS: fast pattern checks that decide
 * whether a conversation is clean, needs a closer look, or is refused outright. A deeper model-based
 * check comes later, before anything is used for training.
 *
 * What it looks for:
 *   - prompt injection: text written to steer a model reading it later ("ignore previous
 *     instructions", fake role headers, chat-template tokens);
 *   - hidden text: zero-width and Unicode tag characters, bidi overrides (invisible instructions);
 *   - malicious code: download-and-run one-liners, encoded PowerShell, destructive commands;
 *   - unsafe links: javascript:/data: URLs, image links that would leak data in their query.
 *
 * Keys, passwords and personal details are NOT judged here: they are removed by redact.ts (the
 * DataClaw-style privacy pass) and reported separately, so they never make a conversation unsafe,
 * only redacted.
 *
 * WHERE a pattern appears matters. Injection text in a USER message is an attempt on the model; the
 * same words in an ASSISTANT message are usually the model explaining what injection is. So user-side
 * injection is "block", assistant-side is "review". Code in a coding chat is normal; only clearly
 * dangerous commands are flagged, and never as "block" on their own.
 *
 * Excerpts are cut short so the panel never repeats a whole attack verbatim.
 */

import type {
  ConversationAnalysis,
  ConversationStats,
  Finding,
  FindingCategory,
  FindingSeverity,
  ShareConversation,
  ShareRole,
  Verdict,
} from "./types";

const MAX_FINDINGS = 25;
/** Very long messages are checked on their first and last part only, to keep the check fast. */
const SCAN_HEAD = 200_000;
const SCAN_TAIL = 50_000;

type Rule = {
  category: FindingCategory;
  label: string;
  re: RegExp;
  /** Severity when found in a user / system message vs an assistant / tool message. */
  user: FindingSeverity;
  other: FindingSeverity;
  mask?: boolean;
  /** Extra check on the match (e.g. Luhn for card numbers). */
  accept?: (m: string) => boolean;
};

const INJECTION: Rule[] = [
  {
    category: "prompt_injection",
    label: "Tells the model to ignore its instructions",
    re: /\b(?:ignore|disregard|forget|override|bypass)\b[^.\n]{0,40}\b(?:all|any|the|your|previous|prior|above|earlier|system|original)\b[^.\n]{0,30}\b(?:instructions?|prompts?|rules|guidelines|directives|context)\b/gi,
    user: "block",
    other: "review",
  },
  {
    category: "prompt_injection",
    label: "Tries to reveal or replace the system prompt",
    re: /\b(?:reveal|print|output|show|repeat|leak|dump)\b[^.\n]{0,30}\b(?:system|hidden|initial|developer)\s+(?:prompt|message|instructions?)\b|\bnew\s+system\s+prompt\b/gi,
    user: "block",
    other: "review",
  },
  {
    category: "prompt_injection",
    label: "Jailbreak persona or mode switch",
    re: /\b(?:you\s+are\s+now\s+(?:in\s+)?(?:DAN|developer\s+mode|jailbroken|unfiltered|uncensored)|do\s+anything\s+now|developer\s+mode\s+(?:enabled|on)|jailbreak(?:ed)?\s+mode|act\s+as\s+an?\s+(?:unrestricted|unfiltered|uncensored)\b)/gi,
    user: "block",
    other: "review",
  },
  {
    category: "prompt_injection",
    label: "Fake chat-template or role tokens",
    re: /<\|(?:im_start|im_end|system|endoftext|start_header_id|eot_id)\|>|\[\/?INST\]|<<\/?SYS>>|^\s*#{2,}\s*(?:system|assistant)\s*(?:prompt|message)?\s*:?\s*$/gim,
    user: "block",
    other: "review",
  },
  {
    category: "prompt_injection",
    label: "Instruction aimed at a model reading this later",
    re: /\b(?:if\s+you\s+are\s+an?\s+(?:AI|LLM|language\s+model|assistant)|(?:AI|LLM)s?\s+reading\s+this|note\s+to\s+(?:the\s+)?(?:AI|model|assistant))\b[^.\n]{0,80}\b(?:must|should|ignore|instead|do\s+not)\b/gi,
    user: "block",
    other: "review",
  },
  {
    category: "prompt_injection",
    label: "Asks to hide something from the user",
    re: /\b(?:do\s+not|don't|never)\s+(?:tell|inform|mention\s+(?:this\s+)?to|reveal\s+(?:this\s+)?to)\s+the\s+user\b/gi,
    user: "review",
    other: "review",
  },
];

const CODE: Rule[] = [
  {
    category: "malicious_code",
    label: "Download-and-run one-liner",
    re: /\b(?:curl|wget)\b[^\n|]{0,200}\|\s*(?:sudo\s+)?(?:ba|z|)sh\b|\b(?:iwr|irm|Invoke-WebRequest|Invoke-RestMethod)\b[^\n|]{0,200}\|\s*(?:iex|Invoke-Expression)\b/gi,
    user: "review",
    other: "info",
  },
  {
    category: "malicious_code",
    label: "Encoded PowerShell command",
    re: /\bpowershell(?:\.exe)?\b[^\n]{0,60}\s-(?:e|en|enc|encodedcommand)\s+[A-Za-z0-9+/=]{40,}/gi,
    user: "review",
    other: "review",
  },
  {
    category: "malicious_code",
    label: "Destructive command",
    re: /\brm\s+-[rf]{2,}\s+(?:--no-preserve-root\s+)?\/(?:\s|$|\*)|:\(\)\s*\{\s*:\|:&\s*\};:|\bmkfs\.\w+\s+\/dev\/sd[a-z]\b|\bdd\s+if=\/dev\/(?:zero|random)\s+of=\/dev\/sd[a-z]\b|\bformat\s+c:\s*\/[qy]/gi,
    user: "review",
    other: "info",
  },
  {
    category: "malicious_code",
    label: "Script or frame tag",
    re: /<\s*(?:script|iframe|object|embed)\b[^>]*>/gi,
    user: "info",
    other: "info",
  },
];

const LINKS: Rule[] = [
  {
    category: "unsafe_link",
    label: "Script link (javascript: or data:text/html)",
    re: /\]\(\s*(?:javascript|vbscript):[^)]*\)|\bhref\s*=\s*["']?\s*(?:javascript|vbscript):|data:text\/html[;,]/gi,
    user: "review",
    other: "review",
  },
  {
    category: "unsafe_link",
    label: "Image link that carries data in its address",
    re: /!\[[^\]]*\]\(\s*https?:\/\/[^)\s]+\?[^)\s]*=[^)\s]{40,}\)/gi,
    user: "review",
    other: "review",
  },
];

const RULES: Rule[] = [...INJECTION, ...CODE, ...LINKS];

/**
 * Invisible characters that can carry instructions a person never sees: zero-width joiners and
 * spaces, word joiner, BOM, bidi overrides/isolates and the Unicode TAG block (U+E0000-E007F, the
 * "ASCII smuggling" range). A few zero-width joiners are normal in emoji, so the threshold is a run
 * or a count, not one character.
 */
const HIDDEN = /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]|\uDB40[\uDC00-\uDC7F]/g;

function mask(s: string): string {
  const t = s.trim();
  if (t.length <= 8) return "•".repeat(t.length);
  return `${t.slice(0, 4)}${"•".repeat(Math.min(12, t.length - 6))}${t.slice(-2)}`;
}

function excerpt(text: string, index: number, match: string, masked: boolean): string {
  const before = text.slice(Math.max(0, index - 40), index).replace(/\s+/g, " ");
  const after = text.slice(index + match.length, index + match.length + 40).replace(/\s+/g, " ");
  const shown = masked ? mask(match) : match.replace(/\s+/g, " ").slice(0, 120);
  return `${index > 40 ? "…" : ""}${before}${shown}${after}${after.length >= 40 ? "…" : ""}`;
}

function scanText(text: string): string {
  if (text.length <= SCAN_HEAD + SCAN_TAIL) return text;
  return `${text.slice(0, SCAN_HEAD)}\n${text.slice(-SCAN_TAIL)}`;
}

function words(s: string): number {
  const m = s.match(/\S+/g);
  return m ? m.length : 0;
}

function stats(conv: ShareConversation): ConversationStats {
  let userMessages = 0;
  let assistantMessages = 0;
  let toolMessages = 0;
  let thinkingBlocks = 0;
  let codeBlocks = 0;
  let images = 0;
  let files = 0;
  let voiceMessages = 0;
  let userWords = 0;
  let assistantWords = 0;
  let total = 0;
  for (const m of conv.messages) {
    const w = words(m.text);
    total += w;
    if (m.kind === "thinking") thinkingBlocks++;
    if (m.voice) voiceMessages++;
    for (const a of m.attachments || []) {
      if (a.kind === "image") images++;
      else files++;
    }
    if (m.kind === "code") codeBlocks++;
    codeBlocks += Math.floor((m.text.match(/```/g)?.length ?? 0) / 2);
    if (m.role === "user") {
      userMessages++;
      userWords += w;
    } else if (m.role === "assistant" && m.kind !== "thinking") {
      assistantMessages++;
      assistantWords += w;
    } else if (m.role === "tool" || m.kind === "tool_call" || m.kind === "tool_result") {
      toolMessages++;
    }
  }

  // Usefulness for training: a real exchange (both sides), some depth, not a one-word prompt.
  const notes: string[] = [];
  let q = 0;
  const isDocs = conv.source === "memory" || conv.source === "project";
  if (isDocs) {
    q = Math.min(100, 30 + Math.round(total / 20));
    notes.push("Notes and documents rather than a conversation");
  } else {
    if (userMessages > 0 && assistantMessages > 0) q += 30;
    else notes.push("Only one side of the conversation");
    const turns = Math.min(userMessages, assistantMessages);
    q += Math.min(25, turns * 5);
    if (turns >= 3) notes.push("Multi-turn exchange");
    const avgUser = userMessages ? userWords / userMessages : 0;
    if (avgUser >= 12) q += 15;
    else if (userMessages) notes.push("Very short prompts");
    if (assistantWords >= 150) q += 15;
    if (thinkingBlocks || toolMessages) {
      q += 10;
      notes.push(thinkingBlocks ? "Includes reasoning" : "Includes tool use");
    }
    if (codeBlocks) {
      q += 5;
      notes.push("Includes code");
    }
    q = Math.min(100, q);
  }

  return {
    turns: Math.min(userMessages, assistantMessages),
    userMessages,
    assistantMessages,
    toolMessages,
    thinkingBlocks,
    codeBlocks,
    images,
    files,
    voiceMessages,
    words: total,
    userWords,
    assistantWords,
    quality: q,
    qualityNotes: notes,
  };
}

const SEVERITY_RANK: Record<FindingSeverity, number> = { block: 3, review: 2, info: 1 };

export function analyseConversation(conv: ShareConversation): ConversationAnalysis {
  const findings: Finding[] = [];
  let more = 0;
  const seen = new Set<string>();

  const push = (f: Finding) => {
    const key = `${f.category}|${f.label}|${f.message}`;
    if (seen.has(key)) return; // one finding per rule per message is enough
    seen.add(key);
    if (findings.length < MAX_FINDINGS) findings.push(f);
    else more++;
  };

  conv.messages.forEach((m, i) => {
    const text = scanText(m.text);
    if (!text) return;
    const userSide = m.role === "user" || m.role === "system";
    const role: ShareRole = m.role;

    for (const rule of RULES) {
      rule.re.lastIndex = 0;
      let hit: RegExpExecArray | null;
      let guard = 0;
      while ((hit = rule.re.exec(text)) && guard++ < 50) {
        if (hit[0].length === 0) {
          rule.re.lastIndex++;
          continue;
        }
        if (rule.accept && !rule.accept(hit[0])) continue;
        push({
          category: rule.category,
          severity: userSide ? rule.user : rule.other,
          label: rule.label,
          message: i,
          role,
          excerpt: excerpt(text, hit.index, hit[0], !!rule.mask),
        });
        break;
      }
    }

    HIDDEN.lastIndex = 0;
    const hidden = text.match(HIDDEN);
    if (hidden && (hidden.length >= 6 || /\uDB40/.test(hidden.join("")))) {
      const tags = hidden.filter((c) => c.charCodeAt(0) === 0xdb40).length;
      push({
        category: "hidden_text",
        severity: tags >= 4 || hidden.length >= 20 ? (userSide ? "block" : "review") : "review",
        label: tags ? `Hidden Unicode tag characters (${tags})` : `Invisible characters (${hidden.length})`,
        message: i,
        role,
        excerpt: `${hidden.length} invisible character${hidden.length === 1 ? "" : "s"} in this message`,
      });
    }
  });

  findings.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || a.message - b.message);

  const st = stats(conv);
  let verdict: Verdict = "clean";
  let reason = "No prompt injection, hidden text or harmful content found.";
  const blocks = findings.filter((f) => f.severity === "block");
  const reviews = findings.filter((f) => f.severity === "review");
  if (blocks.length) {
    verdict = "rejected";
    reason = `Refused: ${blocks[0].label.toLowerCase()} in a ${blocks[0].role} message.`;
  } else if (reviews.length) {
    verdict = "review";
    reason = `Needs a closer look: ${reviews[0].label.toLowerCase()} in a ${reviews[0].role} message.`;
  } else if (st.words === 0) {
    verdict = "review";
    reason = "No readable text was found.";
  }

  return { verdict, reason, findings, moreFindings: more, stats: st };
}
